import { getOrderPaymentUrl, RouteUtil } from '@us/web/shared/utils';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    Injector,
    input,
    signal,
    Signal,
    untracked,
    ViewChild
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslocoModule } from '@ngneat/transloco';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { OrdersActions, selectLoading, selectOrder } from '@us/web/orders/data-access';
import {
    FormattedAddressPipe,
    Order,
    OrderState,
    OrderStateLabelPipe,
    OrderType,
    PaymentType,
    PaymentTypeIconPipe,
    PaymentTypeLabelPipe,
    SafeHtmlPipe,
    Shop
} from '@us/web/shared/data-access';
import {
    TuiButtonModule,
    TuiDataListModule,
    TuiDialogModule,
    TuiDialogService,
    TuiHostedDropdownModule,
    TuiLinkModule,
    TuiLoaderModule,
    TuiSvgModule
} from '@taiga-ui/core';
import { AsyncPipe, JsonPipe, NgClass, NgIf } from '@angular/common';
import { TuiAvatarModule, TuiCarouselModule, TuiTabsModule } from '@taiga-ui/kit';
import {
    FormatCurrencyPipe,
    FormatNumberPipe,
    selectBankTransferInformation,
    selectContentInfoAfterOrder,
    selectDisplayInfoAfterOrder,
    selectPaymentMethods,
    selectPaymentSettingsMpos,
    selectShop,
    selectShopContact
} from '@us/web/shop/data-access';
import { OrderQrcodeComponent, OrderStateComponent, PaymentTypeOptionsComponent, QrPaymentMposOptionsComponent } from '@us/web/orders/ui';
import { NotFoundComponent } from '@us/web/shared/ui';
import { OrderStatusComponent } from '../order-status/order-status.component';
import { IonicModule } from '@ionic/angular';
import { OrderActionsComponent } from '../order-actions/order-actions.component';
import { OrderItemsComponent } from '../order-items/order-items.component';
import { FormatPipeModule, ParseIsoPipeModule } from 'ngx-date-fns';
import { OrderShippingDetailsComponent } from '../order-shipping-details/order-shipping-details.component';
import { isToday, parseISO } from 'date-fns';
import { derivedAsync } from 'ngxtension/derived-async';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';

@UntilDestroy()
@Component({
    selector: 'us-order-detail',
    standalone: true,
    imports: [
        TuiLoaderModule,
        AsyncPipe,
        NgIf,
        TuiTabsModule,
        TranslocoModule,
        TuiCarouselModule,
        RouterLink,
        FormattedAddressPipe,
        FormatCurrencyPipe,
        OrderQrcodeComponent,
        NotFoundComponent,
        OrderStatusComponent,
        IonicModule,
        OrderActionsComponent,
        OrderItemsComponent,
        OrderStateLabelPipe,
        TuiButtonModule,
        ParseIsoPipeModule,
        FormatPipeModule,
        OrderShippingDetailsComponent,
        PaymentTypeIconPipe,
        PaymentTypeLabelPipe,
        OrderStateComponent,
        SafeHtmlPipe,
        JsonPipe,
        TuiSvgModule,
        TuiDialogModule,
        PaymentTypeOptionsComponent,
        NgClass,
        TuiHostedDropdownModule,
        TuiDataListModule,
        TuiAvatarModule,
        FormatNumberPipe,
        TuiLinkModule,
        RouterLink
    ],
    templateUrl: './order-detail.component.html',
    styleUrls: ['./order-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderDetailComponent {
    private readonly store = inject(Store);
    private readonly dialogService = inject(TuiDialogService);
    private readonly injector = inject(Injector);

    @ViewChild('orderTrackingIframe', { static: false }) orderTrackingIframe!: ElementRef;

    readonly orderId = input.required<string>();
    readonly sourceType = input<string>();
    readonly sourceShopId = input<string | number>();

    showHomeBtn = input(true);

    readonly order = derivedAsync(() => this.store.select(selectOrder(this.orderId())), {
        initialValue: null as Order | null
    });
    readonly isLoading = this.store.selectSignal(selectLoading);

    readonly isTopUp = computed(() => {
        return this.order()?.items?.some(item => item.extra && 'card_id' in item.extra) || false;
    });

    readonly topUpInfo = computed(() => {
        if (!this.isTopUp()) {
            return null;
        }
        const order = untracked(this.order);
        if (!order) {
            return null;
        }

        const item = order.items[0];
        return item.extra as {
            amount: number;
            card_id: string;
            bonus_amount: number;
            scan_card_id: string;
            total_amount: number;
            balance_after: number;
            balance_before: number;
        };
    });

    readonly bankTransferInformation = this.store.selectSignal(selectBankTransferInformation);
    readonly paymentMethods: Signal<Array<PaymentType>> = this.store.selectSignal(selectPaymentMethods);
    readonly contactInfo = this.store.selectSignal(selectShopContact);
    readonly shop: Signal<Shop> = this.store.selectSignal(selectShop);
    readonly selectDisplayInfoAfterOrder: Signal<boolean> = this.store.selectSignal(selectDisplayInfoAfterOrder);
    readonly selectContentInfoAfterOrder: Signal<string> = this.store.selectSignal(selectContentInfoAfterOrder);
    readonly optionsQr = this.store.selectSignal(selectPaymentSettingsMpos);

    readonly orderStatusUrl = computed(() => RouteUtil.getOrderStatusRouteUrl(this.orderId()));
    readonly orderTrackingUrl = computed(() => RouteUtil.getOrderTrackingRouteUrl(this.orderId()));

    newPaymentType: PaymentType | null = null;

    readonly allowChangePaymentMethod = computed(() => {
        const order = this.order();
        if (!order) {
            return false;
        }
        return (
            ![
                OrderState.Canceled,
                OrderState.Rejected,
                OrderState.Merged,
                OrderState.PaymentSuccess,
                OrderState.Complete
            ].includes(order.current_state) && isToday(parseISO(order.booking_time as string))
        );
    });

    readonly payUrl = computed(() => {
        const order = this.order();
        return order ? getOrderPaymentUrl(order) : null;
    });

    readonly OrderType = OrderType;
    protected readonly PaymentType = PaymentType;

    showPaymentMethodsDialog = false;

    isLoadingMap = signal(true);

    private readonly orderIdHandler = effect(
        () => {
            this.handleRefresh();
        },
        { allowSignalWrites: true }
    );

    onLoadMap() {
        try {
            // scroll to map
            const doc =
                this.orderTrackingIframe.nativeElement.contentDocument ||
                this.orderTrackingIframe.nativeElement.contentWindow;

            doc.getElementById('map').scrollIntoView({ behavior: 'smooth' });
        } catch (e) {
            // ignore
        }

        this.isLoadingMap.set(false);
    }

    handleRefresh(event: any = null) {
        const id = this.orderId();
        if (!id) {
            return;
        }
        this.store.dispatch(
            OrdersActions.loadOrder({
                id,
                source_type: this.sourceType(),
                source_shop_id: this.sourceShopId(),
                onSuccess: () => {
                    event?.target?.complete();
                },
                onFailure: () => {
                    event?.target?.complete();
                }
            })
        );
    }

    onShowPaymentMethodsDialog() {
        this.showPaymentMethodsDialog = true;
    }

    onChangePaymentMethod() {
        if (this.newPaymentType === null) {
            return;
        }

        const order = this.order();
        if (!order) {
            return;
        }
        if (this.newPaymentType === PaymentType.Mpos) {
            const list_qr = this.optionsQr()?.qr_types || [];
            if (list_qr.length === 1) {
                this.store.dispatch(
                    OrdersActions.updatePaymentMethod({
                        orderId: order.order_id,
                        paymentType: this.newPaymentType as PaymentType,
                        payment_detail: list_qr[0].key
                    })
                );
                return;
            }
            this.dialogService.open(new PolymorpheusComponent(QrPaymentMposOptionsComponent, this.injector), {
                size: 's',
                closeable: false,
                dismissible: false,
                data: {
                    payment_type: this.newPaymentType,
                    is_update_payment: true,
                    order_id: order.order_id
                }
            }).subscribe();
            this.showPaymentMethodsDialog = false;
            return;
        }
        this.store.dispatch(
            OrdersActions.updatePaymentMethod({
                orderId: order.order_id,
                paymentType: this.newPaymentType as PaymentType
            })
        );

        this.showPaymentMethodsDialog = false;
    }

    protected readonly OrderState = OrderState;

    checkTransactionStatus() {
        const id = this.orderId();
        if (!id) {
            return;
        }
        this.store.dispatch(OrdersActions.loadOrderPaymentStatus({ id }));
    }

    handleContact(type = 'phone', data: any) {
        if (type == 'phone') {
            window.location.href = 'tel:' + data;
        } else {
            window.location.href = data;
        }
    }
}
